import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, AnimatePresence } from 'framer-motion';

const HoverFollow = ({ title, column2Text, year,location, imageUrl }) => {
    const [isHovering, setIsHovering] = useState(false);
    
    const containerRef = useRef(null);

    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    const imageSpringX = useSpring(mouseX, { stiffness: 800, damping: 40 });
    const imageSpringY = useSpring(mouseY, { stiffness: 800, damping: 40 });

    const circleSpringX = useSpring(mouseX, { stiffness: 200, damping: 25 });
    const circleSpringY = useSpring(mouseY, { stiffness: 200, damping: 25 });

    const textSpringX = useSpring(mouseX, { stiffness: 180, damping: 20 });
    const textSpringY = useSpring(mouseY, { stiffness: 180, damping: 20 });

    useEffect(() => {
        const handleGlobalMouseMove = (e) => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                mouseX.set(e.clientX - rect.left);
                mouseY.set(e.clientY - rect.top);
            }
        };

        window.addEventListener('mousemove', handleGlobalMouseMove);
        return () => window.removeEventListener('mousemove', handleGlobalMouseMove);
    }, [mouseX, mouseY]);

    const handleMouseEnter = (e) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const startX = e.clientX - rect.left;
        const startY = e.clientY - rect.top;

        mouseX.set(startX);
        mouseY.set(startY);

        imageSpringX.jump(startX);
        imageSpringY.jump(startY);
        circleSpringX.jump(startX);
        circleSpringY.jump(startY);
        textSpringX.jump(startX);
        textSpringY.jump(startY);

        setIsHovering(true);
    };
    
    const handleMouseLeave = () => setIsHovering(false);

    return (
        <div className='max-w-8xl mx-52'>
            <hr className="h-px border-0 bg-zinc-300" />

            <div
                ref={containerRef} 
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className='relative flex justify-between h-36 text-black mx-4 hover:mx-0 hover:text-zinc-600 transition-all duration-500 ease-in-out cursor-pointer'
            >
                <p className='w-2/5 text-5xl font-medium flex items-center px-10'> {title}</p>
                <p className='w-1/5 text-xl flex items-center justify-start'> {column2Text}</p>
                <p className='w-1/5 text-xl flex items-center justify-center'> {year}</p>

                <AnimatePresence>
                    {isHovering && (
                        <>
                            {/* LAYER 1: The Image Mask */}
                            {imageUrl && (
                                <motion.div 
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1, transition: { duration: 0.2 } }}
                                    className="absolute pointer-events-none overflow-hidden bg-transparent"
                                    style={{
                                        x: imageSpringX,
                                        y: imageSpringY,
                                        translateX: "-50%",
                                        translateY: "-50%",
                                        width: '400px',
                                        height: '350px',
                                        top: 0,
                                        left: 0,
                                        zIndex: 10,
                                    }}
                                >
                                    <motion.div
                                        initial={{ y: "100%" }}
                                        // Luxurious, smooth entry
                                        animate={{ y: "0%", transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } }}
                                        // Lightning-fast exit swipe
                                        exit={{ y: "-100%", transition: { duration: 0.1, ease: "easeInOut" } }} 
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            backgroundImage: `url(${imageUrl})`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                        }}
                                    />
                                </motion.div>
                            )}

                            {/* LAYER 2: The Blue Circle */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1, transition: { duration: 0.2 } }}
                                // Snaps out twice as fast
                                exit={{ scale: 0, transition: { duration: 0.1 } }} 
                                className="absolute bg-blue-500 rounded-full shadow-lg pointer-events-none"
                                style={{
                                    x: circleSpringX,
                                    y: circleSpringY,
                                    translateX: "-50%",
                                    translateY: "-50%",
                                    width: '5rem',  
                                    height: '5rem', 
                                    top: 0,
                                    left: 0,
                                    zIndex: 20,
                                }}
                            ></motion.div>

                            {/* LAYER 3: The Floating "VIEW" Text */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1, transition: { duration: 0.2 } }}
                                // Snaps out twice as fast
                                exit={{ scale: 0, transition: { duration: 0.1 } }} 
                                className="absolute pointer-events-none text-white text-xs font-bold whitespace-nowrap flex justify-center items-center"
                                style={{
                                    x: textSpringX,
                                    y: textSpringY,
                                    translateX: "-50%",
                                    translateY: "-50%",
                                    top: 0,
                                    left: 0,
                                    zIndex: 30,
                                }}
                            >
                                VIEW
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </div>

            <hr className="h-px border-0 bg-zinc-300" />
        </div>
    );
};

export default HoverFollow;